import React, { useState, useEffect } from 'react';
import { useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { toast } from 'react-hot-toast';
import doctorService from '../../api/doctorService';

const ConfiguracionImpresion = () => {
  const { currentUser } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({
    logoReceta: '',
    encabezado: '',
    piePagina: '',
    datos: {
      telefono: '',
      direccion: '',
      email: '',
      sitioWeb: '',
      especialidad: ''
    },
    plantillaHTML: '',
    plantillaPDF: null // Nueva propiedad para la plantilla PDF
  });
  const [logoPreview, setLogoPreview] = useState(null);
  const [pdfPreview, setPdfPreview] = useState(null);
  const [pdfFilename, setPdfFilename] = useState('');

  // Estilo general para inputs y textareas como objeto JavaScript en lugar de clases Tailwind
  const inputStyle = {
    width: '100%',
    padding: '8px 16px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    transition: 'border-color 0.2s, box-shadow 0.2s'
  };
  
  const labelStyle = {
    display: 'block',
    color: '#4b5563',
    fontWeight: '500', 
    marginBottom: '8px'
  };
  
  const sectionTitleStyle = {
    fontSize: '1.125rem',
    fontWeight: '600',
    color: '#1f2937',
    marginBottom: '12px'
  };
  
  useEffect(() => {
    const fetchConfiguracion = async () => {
      try {
        setLoading(true);
        const response = await doctorService.getConfiguracionReceta();
        console.log('Respuesta completa:', response);
        
        if (response && response.data) {
          // La respuesta debe tener la estructura { data: {...} }
          const configData = response.data;
          
          // Convertir datos JSON si existe
          const datosObj = configData.datos ? 
            (typeof configData.datos === 'string' ? JSON.parse(configData.datos) : configData.datos) : 
            {
              telefono: '',
              direccion: '',
              email: '',
              sitioWeb: '',
              especialidad: ''
            };
          
          console.log('Datos procesados:', {
            ...configData,
            datos: datosObj
          });
          
          setConfig({
            ...configData,
            datos: datosObj
          });

          
          // Establecer información del PDF si existe
          if (configData.rutaPlantillaPDF) {
            // Extraer el nombre del archivo de la ruta
            const pdfFileName = configData.rutaPlantillaPDF.split('/').pop();
            setPdfFilename(pdfFileName);
            console.log('PDF Template:', pdfFileName);
          }
        } else {
          console.warn('La respuesta no tiene la estructura esperada:', response);
        }
      } catch (error) {
        console.error('Error al cargar la configuración de receta:', error);
        toast.error('No se pudo cargar la configuración');
      } finally {
        setLoading(false);
      }
    };
    
    fetchConfiguracion();
  }, [currentUser]);
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setConfig({
      ...config,
      [name]: value
    });
  };
  
  const handleDatosChange = (e) => {
    const { name, value } = e.target;
    setConfig({
      ...config,
      datos: {
        ...config.datos,
        [name]: value
      }
    });
  };
  
  const handleRemoveLogo = () => {
    setLogoPreview(null);
    setConfig({
      ...config,
      logoReceta: null
    });
  };
  
  const handleRemovePdf = () => {
    setPdfPreview(null);
    setPdfFilename('');
    setConfig({
      ...config,
      plantillaPDF: null,
      rutaPlantillaPDF: null
    });
  };
  
  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
        setConfig({
          ...config,
          logoReceta: reader.result
        });
      };
      reader.readAsDataURL(file);
    }
  };
  
  const handlePdfChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPdfFilename(file.name);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPdfPreview(reader.result);
        setConfig({
          ...config,
          plantillaPDF: reader.result,
          // Guardamos también la ruta para usar con pdf-lib
          rutaPlantillaPDF: `/uploads/pdf-templates/${file.name}`
        });
      };
      reader.readAsDataURL(file);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      
      if (logoPreview && logoPreview.startsWith('data:')) {
        // Si el logo es una cadena base64, es un nuevo archivo
        // Convertir base64 a blob
        const response = await fetch(logoPreview);
        const blob = await response.blob();
        const file = new File([blob], 'logo.png', { type: 'image/png' });
        
        // Crear FormData para enviar el archivo
        const formData = new FormData();
        formData.append('logoReceta', file);
        formData.append('encabezado', config.encabezado);
        formData.append('piePagina', config.piePagina);
        formData.append('datos', JSON.stringify(config.datos));
        formData.append('plantillaHTML', config.plantillaHTML || '');
        
        // Si se ha eliminado el PDF, indicarlo
        if (config.plantillaPDF === null) {
          formData.append('eliminarPlantillaPDF', 'true');
        }
        
        // Agregar plantilla PDF si existe
        if (pdfPreview && pdfPreview.startsWith('data:')) {
          // Convertir base64 a blob
          const pdfResponse = await fetch(pdfPreview);
          const pdfBlob = await pdfResponse.blob();
          const pdfFile = new File([pdfBlob], pdfFilename || 'plantilla_receta.pdf', { type: 'application/pdf' });
          formData.append('plantillaPDF', pdfFile);
        }
        
        await doctorService.actualizarConfiguracionReceta(formData);
      } else if (config.logoReceta === null) {
        // Si el logo se eliminó
        const formData = new FormData();
        formData.append('eliminarLogo', 'true');
        formData.append('encabezado', config.encabezado);
        formData.append('piePagina', config.piePagina);
        formData.append('datos', JSON.stringify(config.datos));
        formData.append('plantillaHTML', config.plantillaHTML || '');
        
        await doctorService.actualizarConfiguracionReceta(formData);
      } else {
        // Convertir datos a string JSON si es necesario para enviar
        const dataToSend = {
          ...config,
          datos: typeof config.datos === 'object' ? config.datos : JSON.parse(config.datos)
        };
        
        await doctorService.guardarConfiguracionReceta(dataToSend);
      }
      
      toast.success('Configuración guardada exitosamente');
    } catch (error) {
      console.error('Error al guardar la configuración:', error);
      toast.error('No se pudo guardar la configuración');
    } finally {
      setSaving(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64 bg-white p-8 rounded-lg shadow-md">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          <p className="mt-4 text-gray-600">Cargando configuración...</p>
        </div>
      </div>
    );
  }
  
  // Estilos inline para reemplazar las clases de Tailwind
  const containerStyle = {
    backgroundColor: 'white',
    borderRadius: '8px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    padding: '20px',
    border: '1px solid #e5e7eb'
  };

  const titleStyle = {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: '#1f2937',
    marginBottom: '20px',
    paddingBottom: '8px',
    borderBottom: '1px solid #e5e7eb'
  };

  return (
    <div style={containerStyle}>
      <h2 style={titleStyle}>Información para Impresión</h2>
      
      <form onSubmit={handleSubmit}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '24px', '@media (min-width: 768px)': { gridTemplateColumns: '1fr 1fr' } }}>
          {/* Logo de Receta */}
          <div style={{ gridColumn: 'span 1' }}>
            <label style={labelStyle}>
              Logo para Impresiones
            </label>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '16px' }}>
              {logoPreview && (
                <div style={{ marginBottom: '16px', padding: '16px', border: '1px solid #d1d5db', borderRadius: '8px', boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)', backgroundColor: 'white' }}>
                  <img 
                    src={logoPreview} 
                    alt="Logo Preview" 
                    style={{ maxHeight: '128px', objectFit: 'contain', margin: '0 auto', display: 'block' }}
                    onError={(e) => {
                      console.error('Error al cargar imagen:', e);
                      // Fallback a un mensaje de error si la imagen no carga
                      e.target.src = 'data:image/svg+xml;charset=UTF-8,%3Csvg%20width%3D%22200%22%20height%3D%22100%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%20200%20100%22%20preserveAspectRatio%3D%22none%22%3E%3Cdefs%3E%3Cstyle%20type%3D%22text%2Fcss%22%3E%23holder_1896554e2b1%20text%20%7B%20fill%3A%23999%3Bfont-weight%3Anormal%3Bfont-family%3AArial%2C%20Helvetica%2C%20Open%20Sans%2C%20sans-serif%2C%20monospace%3Bfont-size%3A10pt%20%7D%20%3C%2Fstyle%3E%3C%2Fdefs%3E%3Cg%20id%3D%22holder_1896554e2b1%22%3E%3Crect%20width%3D%22200%22%20height%3D%22100%22%20fill%3D%22%23f3f3f3%22%3E%3C%2Frect%3E%3Cg%3E%3Ctext%20x%3D%2245%22%20y%3D%2250%22%3EError%20al%20cargar%20imagen%3C%2Ftext%3E%3C%2Fg%3E%3C%2Fg%3E%3C%2Fsvg%3E';
                    }}
                  />
                  <div style={{ marginTop: '12px', textAlign: 'center' }}>
                    <button 
                      type="button"
                      onClick={handleRemoveLogo}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#ef4444',
                        color: 'white',
                        fontSize: '0.75rem',
                        borderRadius: '6px',
                        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                        fontWeight: '500',
                        border: 'none',
                        cursor: 'pointer',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseOver={(e) => e.target.style.backgroundColor = '#dc2626'}
                      onMouseOut={(e) => e.target.style.backgroundColor = '#ef4444'}
                    >
                      Eliminar logo
                    </button>
                  </div>
                </div>
              )}
              <label style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                height: '48px',
                padding: '0 16px',
                backgroundColor: 'white',
                border: '2px dashed #d1d5db',
                borderRadius: '6px',
                cursor: 'pointer',
                transition: 'border-color 0.2s'
              }}
              onMouseOver={(e) => e.target.style.borderColor = '#3f51b5'}
              onMouseOut={(e) => e.target.style.borderColor = '#d1d5db'}
              >
                <span style={{ display: 'flex', alignItems: 'center', columnGap: '8px' }}>
                  <svg xmlns="http://www.w3.org/2000/svg" style={{ width: '24px', height: '24px', color: '#4b5563' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <span style={{ fontSize: '0.875rem', color: '#4b5563', marginLeft: '8px' }}>
                    {logoPreview ? 'Cambiar logo' : 'Seleccionar logo'}
                  </span>
                </span>
                <input 
                  type="file" 
                  name="logoReceta" 
                  style={{ display: 'none' }} 
                  accept="image/*"
                  onChange={handleLogoChange}
                />
              </label>
            </div>
          </div>
          
          {/* Encabezado */}
          <div className="col-span-1">
            <label className={labelStyle}>
              Encabezado
            </label>
            <input
              type="text"
              name="encabezado"
              value={config.encabezado}
              onChange={handleInputChange}
              placeholder="Encabezado para documentos impresos"
              style={inputStyle}
            />
            <p style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '4px' }}>Este texto aparecerá en la parte superior de los documentos impresos</p>
          </div>
        </div>
        
        <div style={{ marginTop: '24px' }}>
          <h3 style={sectionTitleStyle}>Información de Contacto</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '16px', '@media (min-width: 768px)': { gridTemplateColumns: '1fr 1fr' } }}>
            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>
                Teléfono
              </label>
              <input
                type="text"
                name="telefono"
                value={config.datos.telefono}
                onChange={handleDatosChange}
                placeholder="Teléfono de contacto"
                style={inputStyle}
              />
            </div>
            
            <div>
              <label className={labelStyle}>
                Dirección
              </label>
              <input
                type="text"
                name="direccion"
                value={config.datos.direccion}
                onChange={handleDatosChange}
                placeholder="Dirección del consultorio"
                style={inputStyle}
              />
            </div>
            
            <div>
              <label className={labelStyle}>
                Email
              </label>
              <input
                type="email"
                name="email"
                value={config.datos.email}
                onChange={handleDatosChange}
                placeholder="Correo electrónico"
                style={inputStyle}
              />
            </div>
            
            <div>
              <label className={labelStyle}>
                Sitio Web
              </label>
              <input
                type="text"
                name="sitioWeb"
                value={config.datos.sitioWeb}
                onChange={handleDatosChange}
                placeholder="Sitio web"
                style={inputStyle}
              />
            </div>
            
            {/* <div className="md:col-span-2">
              <label className="block text-gray-700 font-medium mb-2">
                Especialidad
              </label>
              <input
                type="text"
                name="especialidad"
                value={config.datos.especialidad}
                onChange={handleDatosChange}
                placeholder="Especialidad médica"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div> */}
          </div>
        </div>
        
        <div style={{ marginTop: '24px' }}>
          <label style={labelStyle}>
            Pie de Página
          </label>
          <textarea
            name="piePagina"
            value={config.piePagina}
            onChange={handleInputChange}
            placeholder="Texto para el pie de página de las recetas"
            rows="3"
            style={{...inputStyle, minHeight: '80px', resize: 'vertical'}}
          ></textarea>
        </div>
        
        {/* Plantilla PDF para recetas */}
        <div style={{ marginTop: '24px' }}>
          <h3 style={sectionTitleStyle}>Plantilla PDF para Recetas</h3>
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Archivo de Plantilla PDF</label>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '16px' }}>
              {pdfFilename && (
                <div style={{ marginBottom: '16px', padding: '16px', border: '1px solid #d1d5db', borderRadius: '8px', boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)', backgroundColor: 'white', width: '100%' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#f97316" width="24" height="24" style={{ marginRight: '12px' }}>
                        <path d="M7 18h10v-2H7v2zM7 14h10v-2H7v2zM7 10h10V8H7v2zM17 3H7C5.9 3 5 3.9 5 5v14c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z"/>
                      </svg>
                      <div>
                        <div style={{ fontWeight: 'bold' }}>{pdfFilename}</div>
                        <div style={{ fontSize: '0.85em', color: '#6b7280' }}>Plantilla PDF para recetas</div>
                      </div>
                    </div>
                    <button 
                      type="button"
                      onClick={handleRemovePdf}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#ef4444',
                        color: 'white',
                        fontSize: '0.75rem',
                        borderRadius: '6px',
                        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                        fontWeight: '500',
                        border: 'none',
                        cursor: 'pointer',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseOver={(e) => e.target.style.backgroundColor = '#dc2626'}
                      onMouseOut={(e) => e.target.style.backgroundColor = '#ef4444'}
                    >
                      Eliminar
                    </button>
                  </div>
                </div>
              )}
              
              <label style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                height: '48px',
                padding: '0 16px',
                backgroundColor: 'white',
                border: '2px dashed #d1d5db',
                borderRadius: '6px',
                cursor: 'pointer',
                transition: 'border-color 0.2s'
              }}
              onMouseOver={(e) => e.target.style.borderColor = '#3f51b5'}
              onMouseOut={(e) => e.target.style.borderColor = '#d1d5db'}
              >
                <span style={{ display: 'flex', alignItems: 'center', columnGap: '8px' }}>
                  <svg xmlns="http://www.w3.org/2000/svg" style={{ width: '24px', height: '24px', color: '#4b5563' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <span style={{ fontSize: '0.875rem', color: '#4b5563', marginLeft: '8px' }}>
                    {pdfFilename ? 'Cambiar plantilla PDF' : 'Seleccionar plantilla PDF'}
                  </span>
                </span>
                <input 
                  type="file" 
                  name="plantillaPDF" 
                  style={{ display: 'none' }} 
                  accept="application/pdf"
                  onChange={handlePdfChange}
                />
              </label>
              <p style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '8px', textAlign: 'center' }}>
              Sube un archivo PDF que servirá como plantilla para las recetas. Las prescripciones se imprimirán sobre esta plantilla.
              </p>
                {pdfFilename && (
                  <div style={{ fontSize: '0.875rem', color: '#10b981', marginTop: '8px', textAlign: 'center', padding: '8px', backgroundColor: '#f0fdf4', borderRadius: '4px', border: '1px solid #86efac' }}>
                    <p><strong>Importante:</strong> Después de guardar, la plantilla estará disponible para generar recetas.</p>
                    <p>La ruta de referencia será: <code style={{ backgroundColor: '#e5e7eb', padding: '2px 4px', borderRadius: '2px' }}>/uploads/pdf-templates/{pdfFilename}</code></p>
                  </div>
                )}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '32px' }}>
          <button
          type="button"
          style={{
            padding: '8px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              color: '#374151',
              marginRight: '8px',
              backgroundColor: 'white',
              fontWeight: '500',
              cursor: saving ? 'default' : 'pointer',
              opacity: saving ? 0.7 : 1,
              transition: 'background-color 0.2s'
            }}
            onMouseOver={(e) => {if (!saving) e.target.style.backgroundColor = '#f3f4f6'}}
            onMouseOut={(e) => e.target.style.backgroundColor = 'white'}
            disabled={saving}
          >
            Cancelar
          </button>
          <button
          type="submit"
          style={{
            padding: '8px 24px',
              backgroundColor: '#3b82f6',
              color: 'white',
              borderRadius: '6px',
              border: 'none',
              fontWeight: '500',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
              cursor: saving ? 'default' : 'pointer',
              opacity: saving ? 0.7 : 1,
              transition: 'background-color 0.2s'
            }}
            onMouseOver={(e) => {if (!saving) e.target.style.backgroundColor = '#2563eb'}}
            onMouseOut={(e) => e.target.style.backgroundColor = '#3b82f6'}
            disabled={saving}
          >
            {saving ? (
              <span style={{ display: 'flex', alignItems: 'center' }}>
                <svg style={{ animation: 'spin 1s linear infinite', marginLeft: '-4px', marginRight: '8px', height: '16px', width: '16px', color: 'white' }} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Guardando...
              </span>
            ) : 'Guardar Configuración'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ConfiguracionImpresion;